import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const API_BASE_URL =
  (process.env.REACT_APP_API_URL || "https://tools.microters.com").replace(/\/$/, "");

const COLORS = {
  background: "#fff9f7",
  navy: "#1e2b49",
  orange: "#ff5a36",
  muted: "#68758c",
  border: "#dce3ec",
};

const TONES = [
  "Professional",
  "Formal",
  "Academic",
  "Friendly",
  "Persuasive",
  "Informative",
  "Inspirational",
  "Casual",
];

const LANGUAGES = [
  "English",
  "Bengali",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Hindi",
  "Arabic",
  "Chinese",
  "Japanese",
  "Korean",
];

const LENGTHS = [
  { value: "short", label: "Short (50-100 words)" },
  { value: "medium", label: "Medium (100-200 words)" },
  { value: "long", label: "Long (200-400 words)" },
  { value: "detailed", label: "Detailed (400-700 words)" },
];

const getErrorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data?.errors?.[0] ||
  (error.code === "ECONNABORTED"
    ? "The request timed out. Please try again."
    : error.message || "Could not generate the essay.");

const countWords = (value) => value.trim().match(/[^\s]+/g)?.length || 0;

export default function AIEssayWriter() {
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("");
  const [language, setLanguage] = useState("");
  const [length, setLength] = useState("medium");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState(null);

  const topicWordCount = useMemo(() => countWords(topic), [topic]);
  const paragraphs = useMemo(
    () => result?.essay?.split(/\n{2,}/).filter(Boolean) || [],
    [result]
  );

  const clearMessages = () => {
    if (error) setError("");
    if (notice) setNotice("");
  };

  const handleGenerate = async (event) => {
    event.preventDefault();
    const cleanTopic = topic.trim();

    if (!cleanTopic) {
      setError("Please enter an essay topic or keywords.");
      return;
    }

    if (!tone) {
      setError("Please select a tone.");
      return;
    }

    if (!language) {
      setError("Please select a language.");
      return;
    }

    setLoading(true);
    setError("");
    setNotice("");
    setResult(null);

    console.log("[AI ESSAY WRITER UI] Sending request:", {
      endpoint: `${API_BASE_URL}/api/essay-writer`,
      topicCharacters: cleanTopic.length,
      tone,
      language,
      length,
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/essay-writer`,
        { topic: cleanTopic, tone, language, length },
        { timeout: 100000 }
      );

      const responseData = response.data?.data;

      if (!response.data?.success || !responseData?.essay?.trim()) {
        throw new Error("The server returned an empty essay.");
      }

      setResult(responseData);
      setNotice("Essay generated successfully.");

      console.log("[AI ESSAY WRITER UI] Essay received:", {
        words: responseData.wordCount,
        characters: responseData.characterCount,
      });
    } catch (requestError) {
      const message = getErrorMessage(requestError);
      setError(message);

      console.error("[AI ESSAY WRITER UI] Request failed:", {
        message,
        status: requestError.response?.status,
        response: requestError.response?.data,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.essay) return;
    const text = `${result.title}\n\n${result.essay}`.trim();

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        textarea.remove();
      }

      setError("");
      setNotice("Essay copied to the clipboard.");
    } catch (copyError) {
      console.error("[AI ESSAY WRITER UI] Copy failed:", copyError);
      setError("Could not copy the essay.");
    }
  };

  const handleDownload = () => {
    if (!result?.essay) return;

    const text = `${result.title}\n\n${result.essay}`.trim();
    const file = new Blob([text], { type: "text/plain;charset=utf-8" });
    const fileUrl = URL.createObjectURL(file);
    const anchor = document.createElement("a");
    anchor.href = fileUrl;
    anchor.download = "ai-generated-essay.txt";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(fileUrl);
    setError("");
    setNotice("Essay downloaded as a TXT file.");
  };

  const handleReset = () => {
    setTopic("");
    setTone("");
    setLanguage("");
    setLength("medium");
    setLoading(false);
    setError("");
    setNotice("");
    setResult(null);
  };

  return (
    <main
      className="min-vh-100 py-5"
      style={{ backgroundColor: COLORS.background, color: COLORS.navy }}
    >
      <Container style={{ maxWidth: "1180px" }}>
       

        <Card className="border rounded-4 shadow-sm" style={{ borderColor: COLORS.border }}>
          <Card.Body className="p-4 p-md-5">
            <Form onSubmit={handleGenerate} noValidate>
              <Form.Group className="mb-4" controlId="essayTopic">
                <Form.Label className="fw-semibold">
                  Essay Topic / Keywords <span style={{ color: COLORS.orange }}>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  maxLength={500}
                  value={topic}
                  disabled={loading}
                  placeholder="e.g. The impact of Artificial Intelligence on modern education..."
                  onChange={(event) => {
                    setTopic(event.target.value);
                    clearMessages();
                  }}
                  style={{ minHeight: "130px", resize: "vertical" }}
                />
                <div
                  className="d-flex justify-content-between small mt-2"
                  style={{ color: COLORS.muted }}
                >
                  <span>{topicWordCount.toLocaleString()} words</span>
                  <span>{topic.length.toLocaleString()}/500 characters</span>
                </div>
              </Form.Group>

              <Row className="g-3 mb-4">
                <Col md={4}>
                  <Form.Group controlId="essayTone">
                    <Form.Label className="fw-semibold">
                      Tone <span style={{ color: COLORS.orange }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={tone}
                      disabled={loading}
                      onChange={(event) => {
                        setTone(event.target.value);
                        clearMessages();
                      }}
                    >
                      <option value="">Select tone...</option>
                      {TONES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="essayLanguage">
                    <Form.Label className="fw-semibold">
                      Language <span style={{ color: COLORS.orange }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={language}
                      disabled={loading}
                      onChange={(event) => {
                        setLanguage(event.target.value);
                        clearMessages();
                      }}
                    >
                      <option value="">Select language...</option>
                      {LANGUAGES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="essayLength">
                    <Form.Label className="fw-semibold">
                      Length <span style={{ color: COLORS.orange }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={length}
                      disabled={loading}
                      onChange={(event) => {
                        setLength(event.target.value);
                        clearMessages();
                      }}
                    >
                      {LENGTHS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {error && (
                <Alert variant="danger" className="py-2" role="alert">
                  {error}
                </Alert>
              )}

              {notice && !result && (
                <Alert variant="success" className="py-2" role="status">
                  {notice}
                </Alert>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-100 border-0 rounded-pill fw-bold py-3 shadow-sm"
                style={{ backgroundColor: COLORS.orange }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Generating Essay...
                  </>
                ) : (
                  <>✦&nbsp; Generate Essay</>
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {result && (
          <Card className="border-0 rounded-4 shadow-sm mt-4 overflow-hidden">
            <Card.Header className="bg-white border-bottom p-4">
              <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between align-items-lg-center">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="text-success fs-5" aria-hidden="true">✓</span>
                    <h2 className="h4 fw-bold mb-0">{result.title || "Generated Essay"}</h2>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {(result.wordCount ?? countWords(result.essay)).toLocaleString()} words
                    </Badge>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {(result.characterCount ?? result.essay.length).toLocaleString()} characters
                    </Badge>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {result.tone || tone}
                    </Badge>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {result.language || language}
                    </Badge>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button variant="outline-secondary" onClick={handleCopy}>
                    Copy
                  </Button>
                  <Button variant="outline-secondary" onClick={handleDownload}>
                    Download TXT
                  </Button>
                  <Button variant="light" className="border" onClick={handleReset}>
                    Start Over
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-4 p-md-5">
              {notice && (
                <Alert variant="success" className="py-2" role="status">
                  {notice}
                </Alert>
              )}

              <article className="fs-5" style={{ color: "#34425b", lineHeight: 1.85 }}>
                {paragraphs.map((paragraph, index) => (
                  <p key={`${paragraph.slice(0, 30)}-${index}`} className="mb-3">
                    {paragraph}
                  </p>
                ))}
              </article>
            </Card.Body>
          </Card>
        )}
      </Container>
    </main>
  );
}
